import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { fetchCurrentUser, updateCurrentUser, fetchMyPlots } from "../api/api";

const EDITABLE_FIELDS = ["full_name", "email", "alternate_email", "landline_number", "address"];

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function CustomerProfile() {

  const { refreshUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [userData, plotsData] = await Promise.all([fetchCurrentUser(), fetchMyPlots()]);
      setProfile(userData);
      setPlots(plotsData);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEditing = () => {
    setSaveError("");
    setSaveSuccess("");
    setForm({
      full_name: profile.full_name || "",
      email: profile.email || "",
      alternate_email: profile.alternate_email || "",
      landline_number: profile.landline_number || "",
      address: profile.address || "",
    });
    setEditing(true);
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const payload = {};
      for (const field of EDITABLE_FIELDS) {
        payload[field] = form[field].trim() || null;
      }
      const updated = await updateCurrentUser(payload);
      setProfile(updated);
      await refreshUser();
      setEditing(false);
      setSaveSuccess("Profile updated.");
    } catch (err) {
      setSaveError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Sidebar />

      <div className="main-content">
        <div className="profile-container">

          <div className="profile-header">
            <h2 className="profile-title">
              Customer Profile
            </h2>

            {!loading && profile && !editing && (
              <button
                className="edit-btn"
                onClick={startEditing}
              >
                Edit Profile
              </button>
            )}

            {editing && (
              <div>
                <button
                  className="save-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save"}
                </button>

                <button
                  className="cancel-btn"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  style={{ marginLeft: "10px" }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {loading && <p>Loading profile…</p>}
          {loadError && <p className="login-error" style={{ color: "red" }}>{loadError}</p>}
          {saveError && <p className="login-error" style={{ color: "red" }}>{saveError}</p>}
          {saveSuccess && <p style={{ color: "#166534", fontWeight: 600 }}>{saveSuccess}</p>}

          {!loading && profile && (
            <>

              <div className="profile-card">

                <div className="profile-row">
                  <strong>Name:</strong>

                  {editing ? (
                    <input
                      type="text"
                      name="full_name"
                      value={form.full_name}
                      onChange={handleChange}
                    />
                  ) : (
                    <span>{profile.full_name}</span>
                  )}
                </div>

                <div className="profile-row">
                  <strong>Mobile:</strong>
                  <span>{profile.mobile}</span>
                </div>

                <div className="profile-row">
                  <strong>Email:</strong>

                  {editing ? (
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                    />
                  ) : (
                    <span>{profile.email || "—"}</span>
                  )}
                </div>

                <div className="profile-row">
                  <strong>Alternate Email:</strong>

                  {editing ? (
                    <input
                      type="email"
                      name="alternate_email"
                      value={form.alternate_email}
                      onChange={handleChange}
                    />
                  ) : (
                    <span>{profile.alternate_email || "—"}</span>
                  )}
                </div>

                <div className="profile-row">
                  <strong>Landline:</strong>

                  {editing ? (
                    <input
                      type="text"
                      name="landline_number"
                      value={form.landline_number}
                      onChange={handleChange}
                    />
                  ) : (
                    <span>{profile.landline_number || "—"}</span>
                  )}
                </div>

                <div className="profile-row">
                  <strong>Address:</strong>

                  {editing ? (
                    <input
                      type="text"
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                    />
                  ) : (
                    <span>{profile.address || "—"}</span>
                  )}
                </div>

                <div className="profile-row">
                  <strong>Referral Code:</strong>
                  <span>{profile.referral_code || "—"}</span>
                </div>

                <div className="profile-row">
                  <strong>Member Since:</strong>
                  <span>{new Date(profile.created_at).toLocaleDateString()}</span>
                </div>

                <div className="profile-row">
                  <strong>Owned Plots:</strong>
                  <span>{plots.length}</span>
                </div>

              </div>

              {plots.length > 0 && (
                <div className="profile-card">
                  <h3 style={{ marginTop: 0 }}>My Plots</h3>

                  {plots.map((plot) => (
                    <div className="profile-row" key={plot.id}>
                      <strong>{plot.plot_number} — {plot.location}</strong>
                      <span>{plot.area_sqft} Sqft • {plot.status}</span>
                    </div>
                  ))}
                </div>
              )}

            </>
          )}

        </div>
      </div>
    </>
  );
}

export default CustomerProfile;
